// The world map, kept on the device (Oct 14 2026). The assembled world blob (/api/map/<wid>?size=…) is 4 MB a 1024-cell
// world before gzip; rather than download it every time the map opens, the last copy is kept in IndexedDB per world and
// size, with the version tag (ETag) the Worker gave it. The map shows that copy at once and then asks the Worker, at most
// every five minutes, whether anything changed (If-None-Match → 304 when nothing did). The map is public game data
// (the chunk files need no sign-in), so the copy is shared by whoever signs in on this device.
//
// Two records per world: `<wid>:<size>:meta` (tag, when it was last confirmed — small, rewritten on every check) and
// `<wid>:<size>:data` (the blob, gzipped where the browser can, rewritten only when the map changed). Everything here is
// best-effort: no IndexedDB (a private window, blocked site data) or any error just means no copy.

const DB = "valor-web";
const STORE = "worldmap";
const VERSION = 1;

export interface SavedMeta {
  etag: string | null;
  /** when the server last confirmed this copy (ms since the epoch) */
  checkedAt: number;
  /** the stored blob is gzipped */
  gz: boolean;
}

let opening: Promise<IDBDatabase | null> | null = null;

function open(): Promise<IDBDatabase | null> {
  if (opening) return opening;
  opening = new Promise<IDBDatabase | null>((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const req = indexedDB.open(DB, VERSION);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return opening;
}

function run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return open().then(
    (db) =>
      new Promise<T | undefined>((resolve) => {
        if (!db) return resolve(undefined);
        try {
          const tx = db.transaction(STORE, mode);
          const req = fn(tx.objectStore(STORE));
          let out: T | undefined;
          if (req) req.onsuccess = () => { out = req.result; };
          tx.oncomplete = () => resolve(out);
          tx.onerror = () => resolve(undefined);
          tx.onabort = () => resolve(undefined);
        } catch {
          resolve(undefined);
        }
      }),
  );
}

const key = (wid: number, size: number, part: "meta" | "data") => `${wid}:${size}:${part}`;

async function pipe(buf: ArrayBuffer, stream: CompressionStream | DecompressionStream): Promise<ArrayBuffer> {
  return new Response(new Blob([buf]).stream().pipeThrough(stream)).arrayBuffer();
}

/** The kept copy of a world's map (the raw VMAP blob), or null. */
export async function readSavedMap(wid: number, size: number): Promise<{ buf: ArrayBuffer; meta: SavedMeta } | null> {
  try {
    const meta = await run<SavedMeta>("readonly", (s) => s.get(key(wid, size, "meta")));
    if (!meta) return null;
    const data = await run<ArrayBuffer>("readonly", (s) => s.get(key(wid, size, "data")));
    if (!data) return null;
    const buf = meta.gz ? await pipe(data, new DecompressionStream("gzip")) : data;
    return { buf, meta };
  } catch {
    return null;
  }
}

/** Keep a new copy of a world's map. */
export async function saveMap(wid: number, size: number, buf: ArrayBuffer, etag: string | null): Promise<void> {
  try {
    const canGz = typeof CompressionStream !== "undefined" && typeof DecompressionStream !== "undefined";
    const data = canGz ? await pipe(buf, new CompressionStream("gzip")) : buf;
    const meta: SavedMeta = { etag, checkedAt: Date.now(), gz: canGz };
    // data first, then meta: a copy without its meta is never read
    await run("readwrite", (s) => s.put(data, key(wid, size, "data")));
    await run("readwrite", (s) => s.put(meta, key(wid, size, "meta")));
  } catch {
    /* best-effort */
  }
}

/** The server said the kept copy is still current: note when. */
export async function touchSavedMap(wid: number, size: number): Promise<void> {
  try {
    const meta = await run<SavedMeta>("readonly", (s) => s.get(key(wid, size, "meta")));
    if (meta) await run("readwrite", (s) => s.put({ ...meta, checkedAt: Date.now() }, key(wid, size, "meta")));
  } catch {
    /* best-effort */
  }
}
